import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Bot } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, CodeBadge, PageHeader } from "@/components/StatusBadge";
import { CoverImage } from "@/components/CoverImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { STATUS, ARTIST_STATUS_OVERRIDE } from "@/constants/catalog";
import { t } from "@/i18n";

export default function ReleasesPage() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const navigate = useNavigate();
  const { can, user } = useAuth();
  const staff = can("admin", "manager");

  const load = () => api.get("/releases", { params: { q: q || undefined, status: status || undefined } }).then((r) => setRows(r.data));
  useEffect(() => { const h = setTimeout(load, 250); return () => clearTimeout(h); }, [q, status]); // eslint-disable-line

  const trigger = async (e, r) => {
    e.stopPropagation();
    try {
      const { data } = await api.post("/jobs", { release_id: r.id });
      toast.success(t("Bot iniciado"));
      navigate(`/bot/${data.id}`);
    } catch (err) { toast.error(errMsg(err)); }
  };
  const label = (s) => t(((user.role === "artist" && ARTIST_STATUS_OVERRIDE[s]) || STATUS[s]).label);
  const filters = ["", "draft", "waiting_2fa", "completed", "approved", "failed"].filter((s) => staff || !["waiting_2fa", "failed"].includes(s));

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Catálogo" title="Lanzamientos">
        <Button onClick={() => navigate("/lanzamientos/nuevo")} data-testid="btn-create-release" className="gap-2"><Plus className="h-4 w-4" /> {t("Nuevo lanzamiento")}</Button>
      </PageHeader>
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Buscar por título, artista o UPC…")} className="pl-9" data-testid="releases-search-input" />
        </div>
        <div className="flex gap-1 flex-wrap">
          {filters.map((s) => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`filter-status-${s || "all"}`}
              className={`chip text-xs rounded-full border px-3 py-1 ${status === s ? "bg-primary/15 border-primary/50 text-primary shadow-[0_0_0_3px_hsl(var(--glow)/0.12)]" : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"}`}>
              {s ? label(s) : t("Todos")}
            </button>
          ))}
        </div>
      </div>
      <div className="panel overflow-hidden">
        <Table data-testid="releases-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[40%]">{t("Lanzamiento")}</TableHead>
              <TableHead>{t("Tipo")}</TableHead>
              <TableHead>{t("Live date")}</TableHead>
              <TableHead>UPC</TableHead>
              <TableHead>ISRC</TableHead>
              <TableHead>{t("Plataformas")}</TableHead>
              <TableHead>{t("Estado")}</TableHead>
              {staff && <TableHead className="text-right">{t("Acciones")}</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center py-14 text-muted-foreground" data-testid="releases-empty">{t("Sin lanzamientos que coincidan.")}</TableCell></TableRow>
            )}
            {rows.map((r) => {
              const isrcs = r.tracks.filter((x) => x.isrc).length;
              const busy = ["queued", "uploading", "waiting_2fa"].includes(r.status);
              const approvedStores = (r.store_status || []).filter((c) => c.state === "published").length;
              return (
                <TableRow key={r.id} className="cursor-pointer group" onClick={() => navigate(`/lanzamientos/${r.id}`)} data-testid={`release-row-${r.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 shrink-0 overflow-hidden rounded-md"><CoverImage fileId={r.cover_file_id} className="h-10 w-10 transition-transform duration-500 group-hover:scale-110" /></div>
                      <div className="min-w-0"><p className="font-medium truncate" data-testid="release-row-title">{r.title}</p><p className="text-xs text-muted-foreground truncate">{r.artist_name}</p></div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{r.release_type}</TableCell>
                  <TableCell className="font-mono text-xs">{r.live_date || "—"}</TableCell>
                  <TableCell><CodeBadge code={r.upc} kind="upc" /></TableCell>
                  <TableCell className="text-xs"><span className={isrcs === r.tracks.length && isrcs > 0 ? "text-primary font-medium" : "text-muted-foreground"}>{isrcs}/{r.tracks.length}</span></TableCell>
                  <TableCell className="text-xs" data-testid="release-row-platforms">
                    {r.store_status?.length ? <span className={r.status === "approved" ? "text-blue-400 font-medium" : "text-muted-foreground"}>{t("{a}/{b} aprobadas", { a: approvedStores, b: r.stores.length || r.store_status.length })}</span> : <span className="text-muted-foreground">{t("{n} seleccionadas", { n: r.stores.length })}</span>}
                  </TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  {staff && <TableCell className="text-right">
                    <Button size="sm" variant={busy ? "secondary" : "outline"} disabled={busy || r.status === "approved"} onClick={(e) => trigger(e, r)} className="gap-1.5" data-testid="btn-trigger-downtown-automation">
                      <Bot className="h-3.5 w-3.5" /> {busy ? t("En proceso") : r.status === "approved" ? t("Aprobado") : r.status === "completed" ? t("Reenviar") : t("Enviar a Downtown")}
                    </Button>
                  </TableCell>}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
